import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Settings, Building, Key, Globe, Hotel, MapPin, Bed, Link, Building2 } from 'lucide-react';
import BrandManagement from '../components/tenant/BrandManagement';
import HotelManagement from '../components/tenant/HotelManagement';
import RoomTypeManagement from '../components/tenant/RoomTypeManagement';
import RoomTypeMappings from '../components/tenant/RoomTypeMappings';
import CompetitorHotelManagement from '../components/tenant/CompetitorHotelManagement';

const TenantSettings = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground">
            Manage your tenant configuration and hotel operations
          </p>
        </div>
      </div>

      {/* Tabbed Interface */}
      <Tabs defaultValue="account" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="account" className="flex items-center space-x-2">
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Account</span>
          </TabsTrigger>
          <TabsTrigger value="brands" className="flex items-center space-x-2">
            <Building className="h-4 w-4" />
            <span className="hidden sm:inline">Brands</span>
          </TabsTrigger>
          <TabsTrigger value="hotels" className="flex items-center space-x-2">
            <Hotel className="h-4 w-4" />
            <span className="hidden sm:inline">Hotels</span>
          </TabsTrigger>
          <TabsTrigger value="rooms" className="flex items-center space-x-2">
            <Bed className="h-4 w-4" />
            <span className="hidden sm:inline">Room Types</span>
          </TabsTrigger>
          <TabsTrigger value="mappings" className="flex items-center space-x-2">
            <Link className="h-4 w-4" />
            <span className="hidden sm:inline">Mappings</span>
          </TabsTrigger>
          <TabsTrigger value="competitors" className="flex items-center space-x-2">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Competitors</span>
          </TabsTrigger>
        </TabsList>

        {/* Account Information Tab */}
        <TabsContent value="account" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Tenant Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Building className="h-5 w-5" />
                  <span>Organization Information</span>
                </CardTitle>
                <CardDescription>
                  Basic information about your organization
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="tenant-name">Organization Name</Label>
                  <Input
                    id="tenant-name"
                    placeholder="Your Hotel Group"
                    defaultValue="Sanur Lodge"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tenant-domain">Domain</Label>
                  <Input
                    id="tenant-domain"
                    placeholder="yourhotel.com"
                    defaultValue="sanurlodge.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tenant-location">Primary Location</Label>
                  <Input
                    id="tenant-location"
                    placeholder="City, Country"
                    defaultValue="Sanur, Bali, Indonesia"
                  />
                </div>
                <Button className="w-full">Update Information</Button>
              </CardContent>
            </Card>

            {/* API Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Key className="h-5 w-5" />
                  <span>API Configuration</span>
                </CardTitle>
                <CardDescription>
                  Manage your API keys and access settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="api-key">API Key</Label>
                  <div className="flex space-x-2">
                    <Input
                      id="api-key"
                      type="password"
                      defaultValue="sk_live_xxxxxxxxxxxxxxxx"
                      readOnly
                    />
                    <Button variant="outline">Copy</Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="webhook-url">Webhook URL</Label>
                  <Input
                    id="webhook-url"
                    placeholder="https://yourhotel.com/webhook"
                  />
                </div>
                <div className="flex space-x-2">
                  <Button variant="outline" className="flex-1">Generate New Key</Button>
                  <Button className="flex-1">Save Settings</Button>
                </div>
              </CardContent>
            </Card>

            {/* Integration Status */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Globe className="h-5 w-5" />
                  <span>Integration Status</span>
                </CardTitle>
                <CardDescription>
                  Status of external integrations and services
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Demand Forecast</p>
                      <p className="text-sm text-muted-foreground">Real-time demand data</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-green-600">Connected</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Competitor Pricing</p>
                      <p className="text-sm text-muted-foreground">Market rate monitoring</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-green-600">Connected</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Event Data Feed</p>
                      <p className="text-sm text-muted-foreground">Local events monitoring</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      <span className="text-sm text-yellow-600">Pending</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Brand Management Tab */}
        <TabsContent value="brands">
          <BrandManagement />
        </TabsContent>

        {/* Hotel Management Tab */}
        <TabsContent value="hotels">
          <HotelManagement />
        </TabsContent>

        {/* Room Types Tab */}
        <TabsContent value="rooms">
          <RoomTypeManagement />
        </TabsContent>

        {/* Room Mappings Tab */}
        <TabsContent value="mappings">
          <RoomTypeMappings />
        </TabsContent>

        {/* Competitors Tab */}
        <TabsContent value="competitors">
          <CompetitorHotelManagement />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TenantSettings;
